import React from 'react';
import {
    TextField,
    MenuItem,
    FormControl,
    FormLabel,
    RadioGroup,
    Radio,
    FormControlLabel,
    FormHelperText,
    Switch,
    Box,
} from '@mui/material';
import { toPersianDigits, toEnglishDigits, formatNumberInputValue, parseNumberInput } from '../../../utils/convertNumbers';
import TimePicker from './StudentRegistration/TimePicker';
import PersianDatePicker from '../../common/PersianDatePicker';
import SearchableAutocomplete from '../../SearchableAutocomplete';

export const renderField = (fieldConfig, fieldKey, formData, handleChange, errors) => {
    // Conditional rendering: if condition exists and returns false, don't render
    if (fieldConfig.condition && !fieldConfig.condition(formData)) return null;

    // Support nested keys like "student_data.image"
    const keys = fieldKey.split('.');
    const value = keys.reduce((obj, key) => (obj ? obj[key] : ''), formData) ?? '';
    const error = keys.reduce((obj, key) => (obj ? obj[key] : null), errors);

    // disabled can be boolean or function(formData)
    const disabled = typeof fieldConfig.disabled === 'function'
        ? fieldConfig.disabled(formData)
        : !!fieldConfig.disabled;

    // helperText can be string or function(formData)
    const helperText =
        typeof fieldConfig.helperText === 'function'
            ? fieldConfig.helperText(formData)
            : fieldConfig.helperText;

    switch (fieldConfig.type) {
        case 'text':
        case 'email':
        case 'password': {
            // Handle digits flag for text fields that might contain numbers
            const hasDigitsFlag = fieldConfig.digits === 'fa-ui/en-wire';
            // Check if this is a numeric field that should have comma formatting
            // Fields like fee, fixed_course_salary should have commas, but registration_sessions, teacher_percentage should not
            const isNumericAmount = hasDigitsFlag && (
                fieldKey === 'fee' || 
                fieldKey === 'fixed_course_salary' || 
                fieldKey.includes('amount') || 
                fieldKey.includes('salary') ||
                fieldKey.includes('fee')
            );
            const displayValue = hasDigitsFlag 
                ? (isNumericAmount ? formatNumberInputValue(value) : toPersianDigits(value || ''))
                : (value || '');
            
            // Set keyboard language: use Farsi keyboard for email/password, or if keepLatinDigits is false
            // This follows the existing pattern where keepLatinDigits controls Latin vs Farsi
            const shouldKeepLatin = fieldConfig.keepLatinDigits || fieldConfig.inputMode === 'latin';
            const isEmailOrPassword = fieldConfig.type === 'email' || fieldConfig.type === 'password';
            // For email/password, always use Farsi keyboard; for others, use Farsi if keepLatinDigits is false
            const useFarsiKeyboard = isEmailOrPassword || !shouldKeepLatin;
            const inputPropsForLanguage = useFarsiKeyboard ? {
                lang: 'fa',
                dir: fieldConfig.type === 'email' ? 'ltr' : 'rtl', // Email should be LTR for proper display
            } : {};
            
            return (
                <TextField
                    key={fieldKey}
                    label={fieldConfig.label}
                    type={fieldConfig.type}
                    inputMode={hasDigitsFlag && isNumericAmount ? "numeric" : undefined}
                    name={fieldKey}
                    value={displayValue}
                    onChange={(e) => {
                        const rawValue = e.target.value;
                        // For numeric amount fields with digits flag, use parseNumberInput for comma handling
                        // For other fields with digits flag, just convert to English
                        const normalizedValue = hasDigitsFlag 
                            ? (isNumericAmount ? parseNumberInput(rawValue, false) : toEnglishDigits(rawValue))
                            : rawValue;
                        handleChange(fieldKey, normalizedValue);
                    }}
                    error={!!error}
                    helperText={error || helperText}
                    variant="outlined"
                    fullWidth
                    required={fieldConfig.required}
                    disabled={disabled}
                    inputProps={inputPropsForLanguage}
                    InputProps={{ title: disabled && helperText ? helperText : undefined }}
                />
            );
        }

        case 'number': {
            // Handle digits flag for Persian/English digit conversion
            const hasDigitsFlag = fieldConfig.digits === 'fa-ui/en-wire';
            // For number fields with digits flag, use comma formatting
            const displayValue = hasDigitsFlag ? formatNumberInputValue(value) : (value || '');
            
            return (
                <TextField
                    key={fieldKey}
                    label={fieldConfig.label}
                    type={hasDigitsFlag ? "text" : "number"} // Use text type for Persian digits
                    inputMode="numeric" // Numeric input mode
                    name={fieldKey}
                    value={displayValue}
                    onChange={(e) => {
                        const rawValue = e.target.value;
                        // For fields with digits flag, use parseNumberInput for comma handling
                        const normalizedValue = hasDigitsFlag ? parseNumberInput(rawValue, false) : rawValue;
                        handleChange(fieldKey, normalizedValue);
                    }}
                    error={!!error}
                    helperText={error || helperText}
                    variant="outlined"
                    fullWidth
                    required={fieldConfig.required}
                    disabled={disabled}
                    inputProps={{
                        min: fieldConfig.min,
                        max: fieldConfig.max,
                        step: fieldConfig.step ?? 1,
                    }}
                    InputProps={{ title: disabled && helperText ? helperText : undefined }}
                />
            );
        }

        case 'radio': {
            return (
                <FormControl
                    key={fieldKey}
                    component="fieldset"
                    error={!!error}
                    fullWidth
                    disabled={disabled}
                >
                    {fieldConfig.label && <FormLabel component="legend">{fieldConfig.label}</FormLabel>}
                    <RadioGroup
                        row={!!fieldConfig.inline}
                        name={fieldKey}
                        value={value}
                        onChange={(e) => handleChange(fieldKey, e.target.value)}
                    >
                        {fieldConfig.options?.map((opt) => (
                            <FormControlLabel
                                key={opt.value}
                                value={opt.value}
                                control={<Radio />}
                                label={opt.label}
                            />
                        ))}
                    </RadioGroup>
                    {(error || helperText) && <FormHelperText>{error || helperText}</FormHelperText>}
                </FormControl>
            );
        }

        case 'switch': {
            // Switches have no helperText slot; render a backend validation
            // error (e.g. «هنرجو هنوز کلاس فعال دارد» on is_active) below the label.
            return (
                <Box key={fieldKey}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={!!value}
                                onChange={(e) => handleChange(fieldKey, e.target.checked)}
                                disabled={disabled}
                            />
                        }
                        label={fieldConfig.label}
                    />
                    {error && (
                        <FormHelperText error sx={{ mx: 1.75 }}>{error}</FormHelperText>
                    )}
                </Box>
            );
        }

        case 'date': {
            // Convert YYYY-MM-DD string to Date object for PersianDatePicker
            // Value comes as Gregorian YYYY-MM-DD string from backend
            const dateValue = value && typeof value === 'string' && value.match(/^\d{4}-\d{2}-\d{2}$/)
                ? new Date(value + 'T00:00:00') // Add time to avoid timezone issues
                : null;

            return (
                <PersianDatePicker
                    key={fieldKey}
                    label={fieldConfig.label}
                    value={dateValue}
                    onChange={(newDate) => {
                        if (newDate && !isNaN(newDate.getTime())) {
                            // Convert Date object back to YYYY-MM-DD string (Gregorian)
                            const formatted = newDate.toISOString().split('T')[0];
                            handleChange(fieldKey, formatted);
                        } else {
                            handleChange(fieldKey, '');
                        }
                    }}
                    helperText={error || helperText}
                    disabled={disabled}
                    required={fieldConfig.required}
                    error={!!error}
                />
            );
        }

        case 'select': {
            return (
                <TextField
                    key={fieldKey}
                    select
                    label={fieldConfig.label}
                    name={fieldKey}
                    value={value}
                    onChange={(e) => handleChange(fieldKey, e.target.value)}
                    error={!!error}
                    helperText={error || helperText}
                    variant="outlined"
                    fullWidth
                    required={fieldConfig.required}
                    disabled={disabled}
                >
                    {fieldConfig.options?.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                            {option.label}
                        </MenuItem>
                    ))}
                </TextField>
            );
        }

        case 'searchable': {
            return (
                <SearchableAutocomplete
                    key={fieldKey}
                    apiUrl={fieldConfig.apiUrl}
                    label={fieldConfig.label}
                    placeholder={fieldConfig.placeholder || 'جستجو...'}
                    value={value}
                    onChange={(newValue) => handleChange(fieldKey, newValue)}
                    getOptionLabel={fieldConfig.getOptionLabel}
                    getOptionValue={fieldConfig.getOptionValue}
                    initialOption={fieldConfig.initialOption}
                    params={fieldConfig.params || {}}
                    disabled={disabled}
                    required={fieldConfig.required}
                    error={error}
                />
            );
        }

        case 'file': {
            return (
                <TextField
                    key={fieldKey}
                    type="file"
                    label={fieldConfig.label}
                    name={fieldKey}
                    onChange={(e) =>
                        // keep original pattern if your hook expects { target: { name, value } }
                        handleChange({ target: { name: fieldKey, value: e.target.files?.[0] ?? null } })
                    }
                    error={!!error}
                    helperText={error || helperText}
                    variant="outlined"
                    fullWidth
                    required={fieldConfig.required}
                    disabled={disabled}
                    InputLabelProps={{ shrink: true }}
                />
            );
        }

        case 'time': {
            return (
                <TimePicker
                    key={fieldKey}
                    label={fieldConfig.label}
                    value={value}
                    onChange={(timeValue) => handleChange(fieldKey, timeValue)}
                    error={!!error}
                    helperText={error || helperText}
                    disabled={disabled}
                />
            );
        }

        default:
            return null;
    }
};
